import { getParentSize } from "../diagram-assets/diagram-positioning-state";
import { getMousePositionRelativeToCanvasContext } from "../../utils/utils";

/**
 * @description
 * @param {CanvasRenderingContext2D} canvas  - canvas 2d ctx
 * @param {FabricDiagram} fabricDiagram - instance of fabric diagram
 * @returns {DiagramCamera} - the new instance of DiagramCamera
 */
export function DiagramCamera(canvas, fabricDiagram) {
    this.WIDTH = 5000;
    this.HEIGHT = 5000;
    this.ACTUAL_WIDTH = 5000;
    this.ACTUAL_HEIGHT = 5000;
    this.fabricDiagram = fabricDiagram;
    this.lastViewport = null;
    this.ctx = canvas.getContext("2d");
    this.canvas = canvas;
    this.mouseDownPressed = false;
    this.showScrollBars = true;
    this.transformScale = window.devicePixelRatio || 1;
    this.transformScaleFactor = window.devicePixelRatio || 1;
}
/**
 * @description returns the scale factor of the camera
 * @returns {Float} - scale factor
 */
DiagramCamera.prototype.getTransformScaleFactor = function () {
    return this.transformScaleFactor;
};
/**
 * @description update the scale factor of the camera
 */
DiagramCamera.prototype.updateTransformScaleFactor = function (data) {
    this.transformScale = data || 1;
    this.transformScaleFactor = data || 1;
};
/**
 * @description Sets the zoom level of the diagram
 * @param {Float} zoomFactor - Indicates by how much it should decrease or increase
 * @param {Object} coords - Mouse coordonates adjusted to diagram context (@see utils file, getMousePositionRelativeToCanvasContext method ).  Optional paramenter.
 * @param {FabricDiagram} fabricDiagramCtx - FabricDiagram instance
 * @param {Object} unadjustedCoords -  Mouse coordonates unadjusted to the canvas context. Optional paramenter.
 * @returns void
 */
DiagramCamera.prototype.setZoomLevel = function (zoomFactor, coords, fabricDiagramCtx, unadjustedCoords) {
    // Check to see if the scale is gonna make the canvas smaller then the parent div
    const parentSize = getParentSize(fabricDiagramCtx.config.containerID);
    const futureCanvasSize = {
        width: this.WIDTH * (this.transformScaleFactor + zoomFactor),
        height: this.HEIGHT * (this.transformScaleFactor + zoomFactor)
    };

    // makes sure it doesnt become smaller then the parent container. Prevent zooming out if it becomes smaller
    if (futureCanvasSize.width < parentSize.width) {
        return false;
    }
    // makes sure it doesnt become smaller then the parent container. Prevent zooming out if it becomes smaller
    if (futureCanvasSize.height < parentSize.height) {
        return false;
    }

    this.transformScaleFactor += zoomFactor;
    this.ACTUAL_WIDTH = (this.WIDTH * this.transformScaleFactor);
    this.ACTUAL_HEIGHT = (this.HEIGHT * this.transformScaleFactor);
    // mouse in / out  at specified coords
    if (coords) {
        const zoomCoordonatesAfterScale = getMousePositionRelativeToCanvasContext(fabricDiagramCtx, unadjustedCoords);
        const differences = { x: coords.x - zoomCoordonatesAfterScale.x, y: coords.y - zoomCoordonatesAfterScale.y };
        if (this.scrollbars.left + differences.x >= 0) {
            this.scrollbars.left += differences.x;
        }
        if (this.scrollbars.top + differences.y >= 0) {
            this.scrollbars.top += differences.y;
        }
        this.scrollbars.draw();
    } else { // mouse in or out from the center of the diagram.
        const visibleWidthAccountedForScale = parentSize.width / this.transformScaleFactor;
        const sidesMargin = (this.ACTUAL_WIDTH - visibleWidthAccountedForScale) / 2;
        this.scrollbars.left = this.scrollbars.top = (sidesMargin > 0 ? sidesMargin : 0);
    }
};
/**
 * @description Modifies the world view by increasing or decreasing it
 * @param {Object} val - Object of type {width, height} representing the values that it should increase / decrease the world size by
 * @returns void
 */
DiagramCamera.prototype.modifyWorldView = function (val) {
    this.WIDTH += val.width;
    this.HEIGHT += val.height;
    this.ACTUAL_WIDTH += (val.width * this.transformScaleFactor);
    this.ACTUAL_HEIGHT += (val.height * this.transformScaleFactor);
};
/**
 * @description Sets a certain width and height as the current world view size
 * @param {Object} val - Object of type {width,height} containing the values to set
 * @returns void
 */
DiagramCamera.prototype.setWorldView = function (val) {
    this.WIDTH = val.width;
    this.HEIGHT = val.height;
    this.ACTUAL_WIDTH = val.width * this.transformScaleFactor;
    this.ACTUAL_HEIGHT = val.height * this.transformScaleFactor;
};
/**
 * @description Scrolls to a certain left and top offset (scroll values)
 * @param {Object} cam - A object of type {left, top} containing the values to scroll to.
 * @returns void
 */
DiagramCamera.prototype.switchCameraFocus = function (cam) {
    this.scrollbars.scrollAt(cam.left, cam.top);
};

/**
 * @description - Initializez the diagram camera. Creates the bottom and right scrollbars
 * @returns void
 */
DiagramCamera.prototype.init = function () {
    const _self = this;
    // an IIFE that creates the scrollbars
    this.scrollbars = (function () {
        const scrollbars = {};
        scrollbars.cameraCtx = _self;
        scrollbars.getCamera = function () {
            return scrollbars.cameraCtx;
        };
        // initial position
        scrollbars.left = 0;
        scrollbars.top = 0;
        /**
     * @description a single constructor for both horizontal and vertical
     * @param {Boolean} vertical - indicates if is vertical or horizotanl
     */
        const ScrollBar = function (vertical) {
            const that = {
                vertical
            };

            that.left = vertical ? _self.canvas.width - 10 : 0;
            that.top = vertical ? 0 : _self.canvas.height - 10;
            that.height = vertical ? _self.canvas.height - 10 : 5;
            that.width = vertical ? 5 : _self.canvas.width - 10;
            that.fill = "#dedede";
            that.visible = true;
            that.cursor = {
                radius: 5,
                fill: "#bababa"
            };
            that.cursor.top = vertical
                ? that.cursor.radius
                : that.top + that.cursor.radius / 2;
            that.cursor.left = vertical
                ? that.left + that.cursor.radius / 2
                : that.cursor.radius;
            /**
       * @description updates the scrolbars offset
       * @returns void
       */
            that.updateScrollbarPosition = function () {
                that.left = vertical ? _self.canvas.width - 10 : 0;
                that.top = vertical ? 0 : _self.canvas.height - 10;
                that.height = vertical ? _self.canvas.height - 10 : 5;
                that.width = vertical ? 5 : _self.canvas.width - 10;
                that.fill = "#dedede";
                that.visible = true;
                that.cursor = {
                    radius: 5,
                    fill: "#bababa"
                };
                that.cursor.top = vertical
                    ? that.cursor.radius
                    : that.top + that.cursor.radius / 2;
                that.cursor.left = vertical
                    ? that.left + that.cursor.radius / 2
                    : that.cursor.radius;
            };
            // drawing the sidebar
            that.draw = function () {
                if (!that.visible) {
                    return;
                }
                // remember to reset the matrix
                const reverseLeft = (-_self.scrollbars.left % 1 === 0) ? -_self.scrollbars.left + -0.5 : -_self.scrollbars.left;
                const reverseTop = (-_self.scrollbars.top % 1 === 0) ? -_self.scrollbars.top + -0.5 : -_self.scrollbars.top;
                _self.ctx.setTransform(1, 0, 0, 1, 0.5, 0.5);
                // you can give it any shape you like, all canvas drawings operations are possible
                _self.ctx.fillStyle = that.fill;
                _self.ctx.fillRect(that.left, that.top, that.width, that.height);
                _self.ctx.beginPath();
                _self.ctx.arc(
                    that.cursor.left,
                    that.cursor.top,
                    that.cursor.radius,
                    0,
                    Math.PI * 2
                );
                _self.ctx.fillStyle = that.cursor.fill;
                _self.ctx.fill();
                _self.ctx.closePath();
                _self.ctx.setTransform(_self.transformScaleFactor, 0, 0, _self.transformScaleFactor, reverseLeft, reverseTop);
            };
            // check if we're hovered
            // currently disbled
            that.isHover = function (x, y) {
                // if (x >= that.left - that.cursor.radius && x <= that.left + that.width + that.cursor.radius &&
                //     y >= that.top - that.cursor.radius && y <= that.top + that.height + that.cursor.radius) {
                // we are so record the position of the mouse and set ourself as the one hovered
                // scrollbars.mousePos = vertical ? y : x;
                // scrollbars.mousePos = y;
                // scrollbars.hovered = that;
                // // scrollbars.hovered = scrollbars.vertical;
                // that.visible = true;
                // return true;
                // }
                // // we were visible last call and no wheel event is happening
                // else if (that.visible && !scrollbars.willHide) {
                //     // Removed making visible false for now
                //     that.visible = false;
                //     // the app should be redrawn
                //     return true;
                // }
            };

            return that;
        };
        // sets the two instances of the scrollbar
        scrollbars.horizontal = ScrollBar(0);
        scrollbars.vertical = ScrollBar(1);

        scrollbars.hovered = null;
        scrollbars.dragged = null;
        scrollbars.mousePos = null;
        // check both of our scrollbars
        // currently disabled functionality, wil never return true since the isHover function body is commented.
        scrollbars.isHover = function (x, y) {
            return this.horizontal.isHover(x, y) || this.vertical.isHover(x, y);
        };
        // draw both of our scrollbars
        scrollbars.draw = function (withPositionUpdate) {
            if (_self.showScrollBars) {
                if (withPositionUpdate) this.horizontal.updateScrollbarPosition();
                this.horizontal.draw();
                if (withPositionUpdate) this.vertical.updateScrollbarPosition();
                this.vertical.draw();
            }
        };
        // check if one of our scrollbars is visible
        scrollbars.visible = function () {
            return this.horizontal.visible || this.vertical.visible;
        };
        // hide it...
        scrollbars.hide = function () {
            // only if we're not using the mousewheel or dragging the cursor
            if (this.willHide || this.dragged) {
                return;
            }
            // Removed making visible false for now
            this.horizontal.visible = false;
            this.vertical.visible = false;
        };

        // get the area's coord relative to our scrollbar
        const toAreaCoord = function (pos, scrollBar) {
            const sbBase = scrollBar.vertical ? scrollBar.top : scrollBar.left;
            const sbMax = scrollBar.vertical ? scrollBar.height : scrollBar.width;
            const areaMax = scrollBar.vertical
                ? _self.ACTUAL_HEIGHT - _self.canvas.height
                : _self.ACTUAL_WIDTH - _self.canvas.width;

            const ratio = (pos - sbBase) / (sbMax - sbBase);
            return areaMax * ratio;
        };

        /**
     * @description get the scrollbar's coord relative to our total area
     * @param {Object} pos - Value of top or left depending on the scrollbar type instance
     * @param {Scrollbar} scrollBar - scrollbar instance. Can be horizontal or vertical
     * @returns
     */
        const toScrollCoords = function (pos, scrollBar) {
            const sbBase = scrollBar.vertical ? scrollBar.top : scrollBar.left;
            const sbMax = scrollBar.vertical ? scrollBar.height : scrollBar.width;
            const areaMax = scrollBar.vertical
                ? _self.ACTUAL_HEIGHT - _self.canvas.height
                : _self.ACTUAL_WIDTH - _self.canvas.width;
            const ratio = pos / areaMax;

            return (sbMax - sbBase) * ratio + sbBase;
        };
        /**
     * @description Scrolls the Camera
     */
        scrollbars.scroll = function () {
            const maxCursorPosHorizontal = this.horizontal.width;
            const maxCursorPosVertical = this.vertical.height;
            this.horizontal.cursor.left = this.mousePosHorizontal <= 0
                ? 0
                : this.mousePosHorizontal >= maxCursorPosHorizontal
                    ? maxCursorPosHorizontal
                    : this.mousePosHorizontal;

            this.vertical.cursor.top = this.mousePosVertical <= 0
                ? 0
                : this.mousePosVertical >= maxCursorPosVertical
                    ? maxCursorPosVertical
                    : this.mousePosVertical;

            // seems ok so tell the app we scrolled
            this.top = toAreaCoord(this.vertical.cursor.top, this.vertical);
            this.left = toAreaCoord(this.horizontal.cursor.left, this.horizontal);
            this.horizontal.visible = true;
            this.vertical.visible = true;

            // if we were called less than the required timeout
            clearTimeout(this.willHide);
            this.willHide = setTimeout(() => {
                scrollbars.willHide = null;
                scrollbars.hide();
                _self.draw();
            }, 500);
            // redraw everything
            _self.draw();
        };
        // because we will hide it after a small time
        scrollbars.willHide;
        // called by the wheel event
        /**
     * @description Scroll at a specific left and top offset
     * @param {Number} left - Scroll to left value
     * @param {Number} top -  Scroll to top value
     */
        scrollbars.scrollAt = function (left, top) {
            if (left) {
                this.left = left < 0 ? 0 : left;
                toScrollCoords(this.left, this.horizontal);
            }
            if (top) {
                this.top = top < 0 ? 0 : top;
                toScrollCoords(this.top, this.vertical);
            }
            _self.draw();
        };
        /**
     * @description scroll by a specific ammount
     * @param {Number} deltaX - Increment / Decrement left offset wih the deltaX value
     * @param {Number} deltaY - Increment / Decrement top offset wih the deltaY value
     */
        scrollbars.scrollBy = function (deltaX, deltaY) {
            // it's not coming from our scrollbars
            this.hovered = null;
            // we're moving horizontally
            if (deltaX) {
                let newLeft = this.left + deltaX;
                if (newLeft < 0) newLeft = 0;
                // make sure we're in the bounds
                this.left = newLeft > _self.ACTUAL_WIDTH - _self.canvas.width ? _self.ACTUAL_WIDTH - _self.canvas.width : newLeft < 0 ? 0 : newLeft;
                // update the horizontal cursor
                this.horizontal.cursor.left = toScrollCoords(this.left, this.horizontal);
                // show our scrollbar
                this.horizontal.visible = true;
            }
            if (deltaY) {
                let newTop = this.top + deltaY;
                if (newTop < 0) newTop = 0;
                this.top = newTop > _self.ACTUAL_HEIGHT - _self.canvas.height ? _self.ACTUAL_HEIGHT - _self.canvas.height : newTop < 0 ? 0 : newTop;
                this.vertical.cursor.top = toScrollCoords(this.top, this.vertical);
                this.vertical.visible = true;
            }
            // if we were called less than the required timeout
            clearTimeout(this.willHide);
            this.willHide = setTimeout(() => {
                scrollbars.willHide = null;
                scrollbars.hide();
                _self.draw();
            }, 500);
            // redraw everything
            _self.draw();
        };
        return scrollbars;
    }());
    // this.registerScrollEvents();
};
/**
 * @description Get the visible area of the canvas
 * @returns {Object} - left represents the horizontal scrollbar offset
 *                   - top represents the vertical scrollbar offset
 */
DiagramCamera.prototype.getVisibleArea = function () {
    const visibleWidth = this.scrollbars.left + (this.canvas.width / this.transformScale);
    const visibleHeight = this.scrollbars.top + (this.canvas.height / this.transformScale);
    return {
        left: this.scrollbars.left,
        width: visibleWidth,
        top: this.scrollbars.top,
        height: visibleHeight
    };
};
/**
 * @description Redraws the scrollbars and the GridView.
 * @param {Boolean} noRedraw - Indicates if should only redraw the GridView or the scrollbars too.
 */
DiagramCamera.prototype.draw = function (noRedraw) {
    // reset everything (clears the canvas + transform + fillStyle + any other property of the context)
    this.canvas.width = this.canvas.width;

    // move our context by the inverse of our scrollbars' left and top property
    const left = (-this.scrollbars.left % 1 === 0) ? -this.scrollbars.left + -0.5 : -this.scrollbars.left;
    const top = (-this.scrollbars.top % 1 === 0) ? -this.scrollbars.top + -0.5 : -this.scrollbars.top;
    this.ctx.setTransform(this.transformScaleFactor, 0, 0, this.transformScaleFactor, left, top);
    this.ctx.textAlign = "center";
    // draw only the visible area
    const visibleWidth = this.scrollbars.left + (this.canvas.width / this.transformScale);
    const visibleHeight = this.scrollbars.top + (this.canvas.height / this.transformScale);
    this.lastViewport = {
        left: this.scrollbars.left,
        width: visibleWidth,
        top: this.scrollbars.top,
        height: visibleHeight
    };
    // redraws the GridView
    if (this.fabricDiagram) {
        this.fabricDiagram.requestViewRedraw(this.lastViewport);
    }
    this.scrollbars.draw(noRedraw);
};
